import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { Select } from '@dynamika-shared/ui/Select/Select';
import {
  collectionTypeTagOptions,
  conditionOperators,
  isLabelCollectionType,
  tagConditionScopeOptions,
  taskSet,
} from './conditionOptions';
import { useFormContext } from 'react-hook-form';
import {
  ComparisonType,
  TaskDefinitionOption,
  TaskDefinitionTag,
} from '@dynamika-shared/types/pmTaskDefinitions';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { TrashIcon } from '@dynamika-shared/ui/icons';
import { LabelCheckboxConditionList } from './LabelCheckboxConditionList';
import { useClinicalTrialInventorySlice } from '@restore/clinicalTrialInventory/clinicalTrialInventory.slice';
import { selectProps } from '../formProps';
import { TaskCondition } from './TaskCondition';
import { useClinicalTrialSlice } from '@restore/clinicalTrial/clinicalTrial.slice';
import { transformModalityLabel } from '@components/SelectLabels/utils';
import { TaskState } from 'src/pages/ClinicalTrial/Readings/TaskDefinition/TaskDefinitionForm/TaskDefinitionForm';
import { useLocation } from 'react-router-dom';
import { sortOptionsByLabel } from 'src/utils/sortOptions';

type SubConditionTagProps = {
  children?: ReactNode;
  className?: string;
  path: string;
  index?: number;
};

const getPathWithoutLastIndex = (path: string) => {
  const lastIndex = path.lastIndexOf('[');

  if (lastIndex !== -1) {
    return path.slice(0, lastIndex);
  }
  return path;
};

const labelConditions = [
  { label: 'All Of', value: 'ALL' },
  { label: 'Any Of', value: 'ANY' },
  { label: 'None Of', value: 'NONE' },
  { label: 'Equals', value: 'EQUALS' },
];

export const SubConditionTag: FC<SubConditionTagProps> = ({ path, index }) => {
  const [labelCondition, setLabelCondition] = useState<string>();
  const { setValue, watch } = useFormContext();

  const currentCondition: TaskDefinitionTag = watch(path);

  const location = useLocation();
  const { formType } = location.state as TaskState;

  const [tags] = useClinicalTrialInventorySlice(state => [state.tags ?? []]);

  const labels = useClinicalTrialSlice(state => state.data?.labelGroups || []);

  const tagOptions = useMemo(
    () => tags.map(tag => ({ label: tag.name, value: tag.id })).sort(sortOptionsByLabel),
    [tags]
  );

  /**
   * When the multi-select label picker replaces the single-label select
   *
   * Plain terms
   * -----------
   * Picking "All Of" / "Any Of" / "None Of" means the condition is about several
   * labels at once, so the reader needs the checkbox list rather than a single
   * dropdown. That applies to every data source except task and measurement ones,
   * which have their own separate form.
   *
   * Technical
   * ---------
   * Previously gated on membership of `UploadFileTypeEnum`, which silently excluded
   * FILE and JSON — they exist only in `CollectionTagEnum`. The list never rendered
   * for them, `shouldShowSingleSelect` took over, and its `updateExpected` overwrote
   * the `.UuidCollection` that `updateComparisonType` had just written back down to
   * `.UuidSingle`, discarding the chosen comparison. Gate on "not a task-like source"
   * instead; `getLabelsForCollectionType` decides which labels each source offers.
   */
  const showLabels = useMemo(
    () =>
      isLabelCollectionType(currentCondition?.collectionType) &&
      !!currentCondition.propertyName &&
      !!currentCondition.expected?.comparisonType,
    [
      currentCondition.expected?.comparisonType,
      currentCondition?.collectionType,
      currentCondition.propertyName,
    ]
  );

  const shouldShowSingleSelect =
    !taskSet.has(currentCondition.collectionType) &&
    labelCondition &&
    currentCondition.propertyName &&
    !showLabels;

  const labelOptions = useMemo(
    () => transformModalityLabel(labels, []),
    [labels]
  );

  const updateCondition = (fieldPath: string, value: unknown) => {
    setValue(`${path}.${fieldPath}`, value);
  };

  const addSubCondition = () => {
    setValue(path, {
      ...currentCondition,
      conditions: currentCondition?.conditions
        ? [...currentCondition.conditions, { type: '.Tag' }]
        : [{ type: '.Tag' }],
    });
  };

  const removeCondition = () => {
    const parentPath = getPathWithoutLastIndex(path);
    const updatedConditions = watch(parentPath).filter(
      (_: unknown, i: number) => i !== index
    );
    setValue(parentPath, updatedConditions);
  };

  const updateConditionByCollectionType = (collectionType: string) => {
    const { type, scope, collectionElementType } = currentCondition;

    const updatedCondition: Partial<TaskDefinitionTag> = {
      type,
      scope,
      collectionElementType,
    };

    if (taskSet.has(collectionType)) {
      updatedCondition.expected = { type: '.Any' };
      updatedCondition.collectionElementType = undefined;
    } else {
      updatedCondition.propertyName = 'LABEL_ID';
      updatedCondition.collectionElementType = { type: '.All' };
    }
    setValue(path, { ...updatedCondition, collectionType });
  };

  const updateComparisonType = (comparisonType: ComparisonType | 'EQUALS') => {
    setLabelCondition(comparisonType);

    const updatedCondition = {
      ...watch(path),
      expected:
        comparisonType === 'EQUALS'
          ? { type: '.UuidSingle', expected: undefined }
          : { type: '.UuidCollection', expected: [], comparisonType },
    };

    setValue(path, updatedCondition);
  };

  const updateExpected = (uuid: string) => {
    setValue(path, {
      ...currentCondition,
      expected: {
        ...currentCondition.expected,
        type: '.UuidSingle',
        expected: uuid,
      },
    });
  };

  useEffect(() => {
    if (
      !Array.isArray(currentCondition.expected?.expected) &&
      formType === 'edit'
    )
      setLabelCondition('EQUALS');
  }, []);
  if (!currentCondition) return null;

  return (
    <div className={'sub-condition-tag ml-3'}>
      <div className={'flex justify-between'}>
        <div className={'flex gap-3 flex-grow flex-wrap'}>
          <Select
            {...selectProps}
            listHeight={160}
            options={conditionOperators}
            value={currentCondition.type}
            onChange={type => updateCondition('type', type)}
            label={'Condition'}
            placeholder={'Select Condition'}
            classNameLabel='pl-1.5'
          />
          {currentCondition.type === '.Tag' && (
            <>
              <Select
                {...selectProps}
                options={tagConditionScopeOptions}
                onChange={scope => updateCondition('scope', scope)}
                value={currentCondition.scope}
                label={'Focus'}
                placeholder={'Select focus'}
                listHeight={280}
              />
              <Select
                {...selectProps}
                options={collectionTypeTagOptions}
                onChange={updateConditionByCollectionType}
                value={currentCondition.collectionType}
                label={'Source'}
                placeholder={'Select source'}
              />

              {currentCondition.collectionType &&
                !taskSet.has(currentCondition.collectionType) && (
                  <Select
                    {...selectProps}
                    options={labelConditions}
                    onChange={updateComparisonType}
                    value={
                      currentCondition.expected?.comparisonType ??
                      labelCondition
                    }
                    label={'Label condition'}
                    placeholder={'Select label condition'}
                  />
                )}

              {taskSet.has(currentCondition.collectionType) && (
                <TaskCondition
                  currentCondition={currentCondition}
                  tagOptions={tagOptions}
                  updateCondition={updateCondition}
                />
              )}
            </>
          )}
          {shouldShowSingleSelect && (
            <Select
              {...selectProps}
              options={labelOptions}
              onChange={updateExpected}
              value={currentCondition.expected?.expected}
              label={'Label'}
              placeholder={'Select label'}
              autoDropdownWidth
            />
          )}
        </div>
        {path !== 'condition' && (
          <TrashIcon
            className='cursor-pointer hover:text-redOrange mt-3 min-w-3'
            width={24}
            height={24}
            onClick={() => removeCondition()}
          />
        )}
      </div>
      {showLabels && (
        <div className={'flex gap-3'}>
          <LabelCheckboxConditionList
            sourceType={currentCondition.collectionType}
            path={path}
          />
        </div>
      )}
      {currentCondition?.type && currentCondition?.type !== '.Tag' && (
        <>
          <>
            {currentCondition.conditions?.map(
              (_: TaskDefinitionTag | TaskDefinitionOption, index: number) => (
                <SubConditionTag
                  key={`${_.type}-${index}`}
                  path={`${path}.conditions[${index}]`}
                  index={index}
                />
              )
            )}
          </>
          <Button
            type={'text'}
            className={'text-1424 text-vividTangelo font-medium mt-2'}
            onClick={addSubCondition}
          >
            + Add Subcondition
          </Button>
        </>
      )}
    </div>
  );
};
